import type { TrackerAdapter, PvOptions } from './types';

/**
 * 事件采样率包装器。在底层 adapter 外套一层采样逻辑，
 * 对指定的事件名按配置的采样率进行概率抽样上报。
 * 未配置的事件名默认全量上报。
 */
export class SamplingAdapter implements TrackerAdapter {
  private adapter: TrackerAdapter;
  private samplingMap: Map<string, number>;

  constructor(
    adapter: TrackerAdapter,
    options: { eventSampling: Record<string, number> },
  ) {
    this.adapter = adapter;
    this.samplingMap = new Map(
      Object.entries(options.eventSampling).map(([key, rate]) => [
        key,
        Math.max(0, Math.min(1, rate)), // clamp to [0, 1]
      ]),
    );
  }

  // ─── 透传方法 ──────────────────────────────────────────

  init(appId: number, options: Record<string, any>): void {
    this.adapter.init(appId, options);
  }

  start(): void {
    this.adapter.start();
  }

  getDefaultProps(): Record<string, any> {
    return this.adapter.getDefaultProps();
  }

  // ─── 采样方法 ──────────────────────────────────────────

  event(eventName: string, properties?: Record<string, any>): void {
    const rate = this.samplingMap.get(eventName);
    // 未配置采样率 → 全量上报
    if (rate === undefined) {
      this.adapter.event(eventName, properties);
      return;
    }
    // 采样率 >= 1 → 全量上报；采样率 <= 0 → 全部丢弃
    if (rate >= 1) {
      this.adapter.event(eventName, properties);
      return;
    }
    if (rate <= 0) {
      return;
    }
    // 概率抽样
    if (Math.random() < rate) {
      this.adapter.event(eventName, properties);
    }
  }

  // ─── 直接透传（不参与采样） ──────────────────────────────

  pageView(options?: PvOptions): void {
    this.adapter.pageView(options);
  }

  login(userId: string): void {
    this.adapter.login(userId);
  }

  logout(): void {
    this.adapter.logout();
  }
}